import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createThread,
  createMessage,
  createRun,
  titleToAssistantIDMap,
} from "./openAIUtils";
import { RenderMarkdown } from "./RenderMarkdown";
import { SuggestedPrompts, getPromptsForType } from "./SuggestedPrompts";
import FloatingPrompts from "./FloatingPrompts";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../config/firebase-config";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  setDoc,
  increment,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import Modal from "./modal";
import { StorageManager, LocalStorageStrategy } from "../storage";

import nurAlHudaImg from "../img/about-nbg.png";
import nurAlHudaForKidsImg from "../img/nuralhudaforkids.png";
import islamicSocraticMethodImg from "../img/islamic_socratic_method.png";
import iqraWithUsImg from "../img/Nuralhuda-applogo.png";
import paliGPTImg from "../img/PaliGPT.png";
import muslimReferenceAIIMG from "../img/muslimReferenceAI.png";
import fiveDThinkingImg from "../img/5dlogotransparent.png";
import nurTutorImg from "../img/nurtutor.png";

import { fileTypeFromBuffer } from "file-type";
import { readFileContent } from "../utils/fileUtils";
import { extFromName, classifyByExt, humanSize } from "../utils/fileDisplay";

import { FaPaperclip, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFileAlt, FaFileCode, FaArrowUp } from "react-icons/fa";
import LanternSvg from "../img/Lantern.svg";

import AudioInterface from "./AudioInterface/AudioInterface"; // Ensure this file exists
import { FaMicrophone } from "react-icons/fa";

import "./chatScreen.css";
import ResourceSelector from './ResourceSelector/ResourceSelector';
import ActiveResourceDisplay from './ResourceSelector/ActiveResourceDisplay';
import { handleAPIError } from './handleError';

// NEW imports for Prompt-ID Responses
import { sendStreamingViaPromptId, base64ToBlobUrl } from '../services/PromptIdBridge';
import { subscribeToAdminSettings, getDefaultSettings } from '../services/adminSettings';
import { useConversation } from '../hooks/useConversation';

const titleToChatbotTypeMap = {
  "Nur Al Huda": "nurAlHuda",
  "Nur Al Huda For Kids": "nurAlHudaForKids",
  "Islamic Socratic Method": "islamicSocraticMethod",
  "AI for Islamic Research": "aiForIslamicResearch",
  "Iqra With Us": "iqraWithUs",
  "Muslim Reference AI": "muslimReferenceAI",
  "PaliGPT": "paliGPT",
  "5D Thinking": "fiveDThinking",
  "Nur Tutor": "nurTutor",
  default: "default",
};

export const titleToImageMap = {
  "Nur Al Huda": nurAlHudaImg,
  "Nur Al Huda For Kids": nurAlHudaForKidsImg,
  "Islamic Socratic Method": islamicSocraticMethodImg,
  "Iqra With Us": iqraWithUsImg,
  "Muslim Reference AI": muslimReferenceAIIMG,
  "PaliGPT": paliGPTImg,
  "5D Thinking": fiveDThinkingImg,
  "Nur Tutor": nurTutorImg,
};

const storageManager = new StorageManager(new LocalStorageStrategy());

// FileChip component for displaying uploaded files
const FileChip = ({ item, onRemove }) => {
  const typeText = `${item.label} • ${humanSize(item.size)}`;
  const statusText = item.status === "uploading" ? "Uploading…" :
                     item.status === "uploaded" ? "Ready" :
                     item.status === "error" ? "Upload failed" : "Local";

  return (
    <div className={`nh-filechip nh-${item.kind}`}>
      {/* left: preview or icon */}
      <div className="nh-filechip-thumb">
        {item.previewUrl
          ? <img src={item.previewUrl} alt={item.name} />
          : <div className="nh-filechip-icon">
              {item.kind === "image" ? <FaFileImage /> :
               item.kind === "pdf"   ? <FaFilePdf /> :
               item.kind === "ppt"   ? <FaFileAlt /> :
               item.kind === "doc"   ? <FaFileWord /> :
               item.kind === "xls"   ? <FaFileExcel /> :
               item.kind === "csv"   ? <FaFileAlt /> :
               item.kind === "txt"   ? <FaFileAlt /> :
               item.kind === "zip"   ? <FaFile /> : <FaFile />}
            </div>}
      </div>

      {/* middle: name + subline */}
      <div className="nh-filechip-body">
        <div className="nh-filechip-name">{item.name}</div>
        <div className="nh-filechip-sub">{typeText}</div>
      </div>

      {/* right: status + remove */}
      <div className="nh-filechip-actions">
        <span className={`nh-filechip-status st-${item.status}`}>{statusText}</span>
        <button className="nh-filechip-x" onClick={onRemove} title="Remove">✕</button>
      </div>
    </div>
  );
};

const ChatScreen = () => {
  const { chatbotType, conversationId: conversationIdParam } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [threadId, setThreadId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showImage, setShowImage] = useState(true);
  const scrollViewRef = useRef(null);
  const assistantTitle = Object.keys(titleToChatbotTypeMap).find(
    (key) => titleToChatbotTypeMap[key] === chatbotType
  );
  const chatbotImage = titleToImageMap[assistantTitle];
  const assistantId = titleToAssistantIDMap[assistantTitle];
  const [user] = useAuthState(auth);

  // Conversation persistence
  const {
    conversationId: activeConversationId,
    conversationTitle,
    isLoadingConversation,
    persistExchange,
    loadConversationMessages,
    resetConversation,
    setConversationTitle,
  } = useConversation({
    user,
    chatbotType,
    assistantTitle,
    conversationIdParam,
    resourceId: null,
  });

  // Track the last sent user message for persistence
  const lastUserMessageRef = useRef('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenReport, setIsModalOpenReport] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [currentPrompts, setCurrentPrompts] = useState([]);
  const [reportFeedbackMessage, setReportFeedbackMessage] = useState("");
  const [reportedMessage, setReportedMessage] = useState("");
  const [totalPromptCount, setTotalPromptCount] = useState(0);
  const [lastResetDate, setLastResetDate] = useState(null);
  const [accountType, setAccountType] = useState("");
  const [maxPrompts, setMaxPrompts] = useState(5);
  const [adminPromptLimits, setAdminPromptLimits] = useState(getDefaultSettings().promptLimits);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const [showFloatingPrompts, setShowFP] = useState(true);
  const [userMemory, setUserMemory] = useState('');

  // NEW state for Prompt-ID Responses
  const [nhConversationId, setNhConversationId] = useState(null);
  const [genImages, setGenImages] = useState([]); // [{url, caption}]
  const [isThinking, setIsThinking] = useState(false);
  const [reasoningText, setReasoningText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const threadIdRef = useRef(null);
  const nhConversationIdRef = useRef(null);
  const genImagesRef = useRef([]);

  // Step 2: Add state inside ChatScreen component
  const [showAudioOverlay, setShowAudioOverlay] = useState(false);
  // Step 3: Add a handler function
  const handleMicClick = () => {
    setShowAudioOverlay(true);
  };

  const handleAudioClose = () => {
    setShowAudioOverlay(false);
  };

  const handleAudioSubmit = (transcript, responseText) => {
    if (transcript) {
      /* USER SPEECH */
      setMessages((prev) => [...prev, { sender: "user", text: transcript }]);
      setShowImage(false);
      setShowFP(false);
      return;
    }

    if (responseText) {
      /* ASSISTANT STREAM / END */
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.sender === "assistant" && isSending) {
          /* still streaming – overwrite bubble */
          return [...prev.slice(0, -1), { sender: "assistant", text: responseText }];
        }
        /* new turn – append */
        return [...prev, { sender: "assistant", text: responseText }];
      });
    }
  };

  // Add a function to handle when audio response is complete
  const handleAudioResponseComplete = () => {
    console.log("Audio response complete");
    setShowImage(false);
    setShowFP(false);
  };
  const [selectedResource, setSelectedResource] = useState(null);

  const updateThreadId = useCallback((nextThreadId) => {
    threadIdRef.current = nextThreadId;
    setThreadId(nextThreadId);
  }, []);

  const updateNhConversationId = useCallback((nextConversationId) => {
    nhConversationIdRef.current = nextConversationId;
    setNhConversationId(nextConversationId);
  }, []);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    nhConversationIdRef.current = nhConversationId;
  }, [nhConversationId]);

  useEffect(() => {
    genImagesRef.current = genImages;
  }, [genImages]);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    scrollViewRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Set available prompts based on chatbot type
  useEffect(() => {
    setCurrentPrompts(getPromptsForType(chatbotType));
  }, [chatbotType]);

  // Show floating prompts only if there are no messages
  useEffect(() => {
    if (messages.length > 0) {
      setShowFP(false);
      setShowImage(false);
    } else {
      setShowFP(true);
      setShowImage(true);
    }
  }, [messages]);

  // Load conversation from URL param
  useEffect(() => {
    if (!conversationIdParam || !user) return;
    let cancelled = false;
    (async () => {
      const result = await loadConversationMessages(conversationIdParam);
      if (cancelled || !result?.conv) return;
      setMessages(result.messages);
      // Restore API state so user can continue chatting
      if (result.conv.threadId) updateThreadId(result.conv.threadId);
      if (result.conv.nhConversationId) updateNhConversationId(result.conv.nhConversationId);
    })();
    return () => { cancelled = true; };
  }, [conversationIdParam, user, loadConversationMessages, updateNhConversationId, updateThreadId]);

  // Reset conversation when resource changes
  const handleResourceChange = (resourceId, shouldReset) => {
    // Only reset if shouldReset flag is true
    if (shouldReset) {
      // Reset state
      setMessages([]);
      updateThreadId(null);
      setCurrentMessage("");
      setShowImage(true);
      setShowFP(true);
      setIsSending(false);
      accumulatedRef.current = ''; // Clear the accumulated ref too
      
      // Import resources dynamically to get the resource name
      import('../data/paliResources.json').then(resources => {
        // Find the resource name for the user notification
        const resourceData = resources.default.find(r => r.id === resourceId);
        const resourceName = resourceData ? resourceData.name : 'new resource';
        
        // Add a system message to inform the user about the change
        setMessages([{
          sender: 'assistant',
          text: `Switched to ${resourceName}. This conversation has been reset with a new knowledge base.`
        }]);
        
        console.log(`Switched resource to ${resourceId} - conversation reset`);
      });
    }
    
    // Update the selected resource
    setSelectedResource(resourceId);
  };

  const checkAndResetPromptCount = async (userData) => {
    const now = new Date();
    const today = now.toDateString();

    let userLastResetDate = null;
    if (userData && userData.lastResetDate) {
      userLastResetDate = userData.lastResetDate.toDate().toDateString();
    } else {
      userLastResetDate = storageManager.load("lastResetDate") || null;
    }

    if (userLastResetDate !== today) {
      console.log("Resetting prompt count for the new day");
      if (user) {
        await setDoc(
          doc(db, "users", user.uid),
          {
            totalPromptCount: 0,
            lastResetDate: now,
          },
          { merge: true }
        );
      } else {
        setTotalPromptCount(0);
        storageManager.save("totalPromptCount", 0);
        storageManager.save("lastResetDate", today);
      }
      setLastResetDate(today);
      setTotalPromptCount(0);
    } else {
      setLastResetDate(today);
    }
  };

  const fetchUserData = async () => {
    let userData = null;
    let sessionPromptCount = 0;
    let sessionLastResetDate = new Date().toDateString();
    let sessionAccountType = "";

    if (user) {
      console.log(`Fetching data for user: ${user.uid}`);
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        userData = userDoc.data();
        console.log("User data:", userData);
      } else {
        console.log("User document does not exist, creating a new one");
        const currentDate = new Date();
        await setDoc(doc(db, "users", user.uid), {
          totalPromptCount: 0,
          lastResetDate: currentDate,
          account: "",
        });
        userData = {
          totalPromptCount: 0,
          lastResetDate: currentDate,
          account: "",
        };
      }

      await checkAndResetPromptCount(userData);

      sessionPromptCount = userData.totalPromptCount || 0;
      sessionLastResetDate = userData.lastResetDate
        ? userData.lastResetDate.toDate().toDateString()
        : new Date().toDateString();
      sessionAccountType = userData.account || "";
      setUserMemory(userData.memory || '');
    } else {
      sessionPromptCount = storageManager.load("totalPromptCount") || 0;
      sessionLastResetDate =
        storageManager.load("lastResetDate") || new Date().toDateString();
      sessionAccountType = storageManager.load("account") || "";
      await checkAndResetPromptCount();
    }

    setTotalPromptCount(sessionPromptCount);
    setLastResetDate(sessionLastResetDate);
    setAccountType(sessionAccountType);

    const isNurAlHuda = chatbotType === "nurAlHuda";
    const isNurAlHudaForKids = chatbotType === "nurAlHudaForKids";
    const isPaliGPT = chatbotType === "paliGPT";

    // Use admin settings for prompt limits, with fallback to defaults
    if (isPaliGPT) {
      setMaxPrompts(adminPromptLimits.paliGPT || Infinity);
    } else {
      // Map account type to admin prompt limits
      const accountTypeKey = sessionAccountType || "free";
      const limit = adminPromptLimits[accountTypeKey] || adminPromptLimits.free || 5;
      setMaxPrompts(limit);
    }
  };

  // Subscribe to admin settings for prompt limits
  useEffect(() => {
    const unsubscribe = subscribeToAdminSettings((settings) => {
      if (settings?.promptLimits) {
        setAdminPromptLimits(settings.promptLimits);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [user, adminPromptLimits]);

  // Cleanup generated images and file previews on unmount
  useEffect(() => {
    return () => {
      genImages.forEach(img => URL.revokeObjectURL(img.url));
      uploadedFiles.forEach(f => { 
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); 
      });
    };
  }, [genImages, uploadedFiles]);

  const accumulatedRef = useRef(""); // Mutable accumulator — never causes renders
  const [liveStreamText, setLiveStreamText] = useState(''); // drives streaming bubble
  const [isStreaming, setIsStreaming] = useState(false);    // drives the interval

  // setInterval (macrotask) syncs ref → state so the browser can paint between ticks.
  // Calling setState inside the synchronous SSE reader loop is a microtask — the browser
  // cannot paint until the entire loop finishes, so all text appears at once.
  // The interval fires as a macrotask, yielding to the browser between each flush.
  useEffect(() => {
    if (!isStreaming) return;
    const id = setInterval(() => {
      const text = accumulatedRef.current;
      if (text) setLiveStreamText(text);
    }, 50); // ~20 fps
    return () => clearInterval(id);
  }, [isStreaming]);

  const removeStreamingPlaceholder = useCallback(() => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.sender === "assistant" && last.streaming) {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);

  const clearStreamingUi = useCallback(() => {
    accumulatedRef.current = "";
    setLiveStreamText('');
    setIsStreaming(false);
    setIsThinking(false);
    setReasoningText('');
    setIsProcessing(false);
  }, []);

  const startAssistantStream = useCallback(() => {
    console.log('[stream] started');
    accumulatedRef.current = "";
    setLiveStreamText('');
    setIsStreaming(true);
    setIsThinking(false);
    setReasoningText('');
    setIsProcessing(false);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.sender === "assistant" && last.streaming) {
        return prev;
      }
      return [...prev, { sender: "assistant", text: "", streaming: true }];
    });
    setIsSending(true);
  }, []);

  const appendAssistantDelta = useCallback((delta) => {
    if (!delta) return;
    console.log('[stream] delta:', delta);
    accumulatedRef.current += delta;
    // Do NOT call setLiveStreamText here — the setInterval (macrotask) handles it.
    // setState inside the synchronous SSE loop batches all updates into one render.
  }, []);

  const finalizeAssistantStream = useCallback(() => {
    const finalText = accumulatedRef.current.trim();
    console.log('[stream] finalized, length:', finalText.length);
    const persistedThreadId = threadIdRef.current;
    const persistedNhConversationId = nhConversationIdRef.current;
    const persistedImages = genImagesRef.current.map((img) => ({
      url: img.url,
      caption: img.caption,
    }));

    setIsStreaming(false);

    setMessages((prev) => {
      const last = prev[prev.length - 1];

      if (last?.sender === "assistant" && last.streaming) {
        if (!finalText) {
          return prev.slice(0, -1);
        }

        return [...prev.slice(0, -1), { sender: "assistant", text: finalText }];
      }

      if (finalText) {
        return [...prev, { sender: "assistant", text: finalText }];
      }

      return prev;
    });

    // Persist exchange to Firestore
    if (finalText && lastUserMessageRef.current) {
      persistExchange({
        userText: lastUserMessageRef.current,
        assistantText: finalText,
        images: persistedImages,
        threadId: persistedThreadId,
        nhConversationId: persistedNhConversationId,
      }).catch(err => console.error('Failed to persist exchange:', err));
    }

    accumulatedRef.current = "";
    setLiveStreamText('');
    setIsThinking(false);
    setReasoningText('');
    setIsProcessing(false);
    lastUserMessageRef.current = '';
    setIsSending(false);
  }, [persistExchange]);

  // unified "delta" handler for prompt-id flow
  const handlePromptIdDelta = (delta) => {
    appendAssistantDelta(delta);
  };

  // tiny image handler (optional UI)
  const handlePromptIdImage = (img) => {
    // img = { kind:'base64', base64, mime, revised_prompt? }
    if (img?.kind === 'base64' && img.base64) {
      const url = base64ToBlobUrl(img.base64, img.mime || 'image/png');
      if (url) {
        setGenImages((prev) => {
          const next = [...prev, {
            url,
            caption: img.revised_prompt || ''
          }];
          genImagesRef.current = next;
          return next;
        });
      }
    }
  };

  const getFileTypeDisplay = (mimeType) => {
    if (!mimeType) return 'Unknown';
    
    // Clean up common MIME types to show friendly names
    const typeMap = {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/msword': 'DOC',
      'application/pdf': 'PDF',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/vnd.ms-powerpoint': 'PPT',
      'text/plain': 'TXT',
      'text/csv': 'CSV',
      'application/json': 'JSON',
      'text/markdown': 'MD',
      'text/html': 'HTML',
      'text/css': 'CSS',
      'application/javascript': 'JS',
      'text/xml': 'XML',
      'application/xml': 'XML'
    };
    
    // Check if it's an image
    if (mimeType.startsWith('image/')) {
      const format = mimeType.split('/')[1].toUpperCase();
      return format === 'JPEG' ? 'JPG' : format;
    }
    
    return typeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'File';
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFile />;
    
    if (fileType.includes('pdf')) return <FaFilePdf />;
    if (fileType.includes('word') || fileType.includes('document')) return <FaFileWord />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <FaFileExcel />;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return <FaFileAlt />;
    if (fileType.includes('image')) return <FaFileImage />;
    if (fileType.includes('text') || fileType.includes('plain')) return <FaFileAlt />;
    if (fileType.includes('csv')) return <FaFileAlt />;
    if (fileType.includes('json')) return <FaFileCode />;
    if (fileType.includes('markdown') || fileType.includes('md')) return <FaFileAlt />;
    if (fileType.includes('html')) return <FaFileCode />;
    if (fileType.includes('css')) return <FaFileCode />;
    if (fileType.includes('javascript') || fileType.includes('js')) return <FaFileCode />;
    if (fileType.includes('xml')) return <FaFileCode />;
    
    return <FaFile />;
  };

  const handleNewMessage = (message) => {
    if (message.text === "END_TOKEN") {
      finalizeAssistantStream();
    } else {
      appendAssistantDelta(message.text);
    }
  };

  const handleError = (error) => {
    console.error("Stream error:", error);
    removeStreamingPlaceholder();
    clearStreamingUi();
    handleAPIError(error, setMessages, updateThreadId, setIsSending);
  };

  const handleSendMessage = async () => {
    if (isSending || (!currentMessage.trim() && uploadedFiles.length === 0)) return;
    
    // Define all assistant types once (no re-declarations later)
    const isNurAlHuda = chatbotType === "nurAlHuda";
    const isNurAlHudaForKids = chatbotType === "nurAlHudaForKids";
    const isPaliGPT = chatbotType === "paliGPT";
    const effectiveSelectedResource = isPaliGPT 
      ? (selectedResource || 'asst_ShMxEdN8gnWONFOVVFv8dKTJ')  // Default PaliGPT resource
      : null;

    console.log(`Chatbot type: ${chatbotType}`);
    console.log(`Is Nur Al Huda: ${isNurAlHuda}`);
    console.log(`Sending message with resource ID: ${effectiveSelectedResource}`);
    console.log(`Current thread ID: ${threadId || 'null (new thread will be created)'}`);
    
    setIsSending(true);

    try {
      if (!user) {
        if (!isPaliGPT && totalPromptCount >= maxPrompts) {
          setModalMessage(
            `You have reached the daily limit of ${maxPrompts} prompts. Please log in to continue using the service.`
          );
          setIsModalOpen(true);
          setIsSending(false);
          return;
        }
        setTotalPromptCount((prevCount) => prevCount + 1);
        storageManager.save("totalPromptCount", totalPromptCount + 1);
      } else {
        console.log("Fetching latest prompt count from Firestore");
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();
        const currentPromptCount = userData.totalPromptCount || 0;
        const userAccount = userData.account || "";

        // Use admin settings for prompt limits
        let userMaxPrompts = maxPrompts;
        if (isPaliGPT) {
          userMaxPrompts = adminPromptLimits.paliGPT || Infinity;
        } else {
          const accountTypeKey = userAccount || "free";
          userMaxPrompts = adminPromptLimits[accountTypeKey] || adminPromptLimits.free || 5;
        }

        if (currentPromptCount >= userMaxPrompts) {
          setModalMessage(
            `You have reached the daily limit of ${userMaxPrompts} prompts. ${
              userAccount === "pro" || userAccount === "enterprise"
                ? "Wait until tomorrow to learn more!"
                : "Upgrade to a premium or enterprise account for more prompts!"
            }`
          );
          setIsModalOpen(true);
          setIsSending(false);
          return;
        }

        console.log("Incrementing total prompt count");
        await updateDoc(doc(db, "users", user.uid), {
          totalPromptCount: increment(1),
        });
        setTotalPromptCount((prevCount) => prevCount + 1);
        storageManager.save("totalPromptCount", totalPromptCount + 1);
      }

      // Process uploaded files - only for non-Nur Al Huda assistants
      let filesContent = "";
      let fileProcessingErrors = [];
      
      if (uploadedFiles.length > 0 && !isNurAlHuda) {
        // For other assistants, process files using fileUtils
        const fileContentsPromises = uploadedFiles.map(async (file) => {
          try {
            const content = await readFileContent(file, chatbotType);
            return `Content from ${file.name}:\n${content}`;
          } catch (error) {
            console.error(`Error processing file ${file.name}:`, error);
            fileProcessingErrors.push(`${file.name}: ${error.message}`);
            return null;
          }
        });
        const fileContents = await Promise.all(fileContentsPromises);
        filesContent = fileContents.filter(Boolean).join("\n\n");
      }

      if (fileProcessingErrors.length > 0) {
        setModalMessage(
          `The following files could not be processed:\n${fileProcessingErrors.join(
            "\n"
          )}`
        );
        setIsModalOpen(true);
      }

      const userMessageText = currentMessage.trim();
      let fullMessage = isNurAlHuda
        ? userMessageText // For Nur Al Huda, just use the text message
        : [userMessageText, filesContent].filter(Boolean).join("\n\n"); // For others, include file content

      // Inject memory context on the first message of a new conversation
      if (!activeConversationId && userMemory && messages.length === 0) {
        fullMessage = `[User Context: ${userMemory}]\n\n${fullMessage}`;
      }
      
      if (!fullMessage) {
        setIsSending(false);
        return;
      }

      const displayMessageText =
        userMessageText || uploadedFiles.map((f) => f.name).join(", ");
      const sentFiles = uploadedFiles.length > 0
        ? uploadedFiles.map(f => ({ name: f.name, kind: f.kind, size: f.size }))
        : undefined;
      const userMessage = { sender: "user", text: displayMessageText, files: sentFiles };

      // Track for persistence
      lastUserMessageRef.current = displayMessageText;

      setMessages((prevMessages) => [
        ...prevMessages,
        userMessage,
        { sender: "assistant", text: "", streaming: true },
      ]);
      setCurrentMessage("");
      setShowImage(false);
      setUploadedFiles([]);

      console.log(`About to branch - isNurAlHuda: ${isNurAlHuda}, chatbotType: ${chatbotType}`);
      
      if (isNurAlHuda) {
        // ---- Prompt-ID branch (NO threads/runs) ----
        const userText = fullMessage; // from your existing build logic

        // For Nur Al Huda, we can pass file IDs to the code interpreter
        // The files are already uploaded and handled by the framework
        const promptIdFileIds = uploadedFiles
          .filter(f => f.status === 'uploaded' && f.fileId)
          .map(f => f.fileId);

        startAssistantStream();

        try {
          await sendStreamingViaPromptId({
            userMessage: userText,
            conversationId: nhConversationId,
            fileIds: promptIdFileIds,
            onDelta: handlePromptIdDelta,
            onImage: handlePromptIdImage,
            onReasoning: (status, data) => {
              if (status === 'thinking') {
                setIsThinking((prev) => {
                  if (!prev) {
                    setReasoningText('');
                  }
                  return true;
                });
              } else if (status === 'thinking_delta') {
                setReasoningText(prev => prev + data);
              } else if (status === 'thinking_done') {
                setIsThinking(false);
                setReasoningText('');
              }
            },
            onImageGeneration: (status, data) => {
              if (status === 'in_progress') {
                setIsProcessing(true);
              } else if (status === 'completed' || status === 'failed') {
                setIsProcessing(false);
              }
            },
            onStatus: (status, response) => {
              // Track the Responses API conversation id as soon as the stream exposes it.
              if (!nhConversationIdRef.current && response?.id) {
                updateNhConversationId(response.id);
              }
            },
            onComplete: (response) => {
              finalizeAssistantStream();
            },
            onError: (err) => {
              console.error("Prompt-ID error:", err);
              removeStreamingPlaceholder();
              clearStreamingUi();
              handleAPIError(err, setMessages, updateThreadId, setIsSending);
            }
          });
          return; // do not fall through
        } catch (error) {
          console.error("Prompt-ID unexpected error:", error);
          removeStreamingPlaceholder();
          clearStreamingUi();
          handleAPIError(error, setMessages, updateThreadId, setIsSending);
          return;
        }
      }

      // ---- existing Assistants flow (UNCHANGED) ----
      let localThreadId = threadId;
      if (!localThreadId) {
        try {
          console.log(`Creating new thread for resource: ${effectiveSelectedResource}`);
          // Pass the effective selected resource ID for PaliGPT
          const threadResponse = await createThread(assistantTitle, effectiveSelectedResource);
          if (threadResponse?.id) {
            console.log(`Successfully created thread: ${threadResponse.id}`);
            updateThreadId(threadResponse.id);
            localThreadId = threadResponse.id;
            if (user) {
              const userRef = doc(db, "users", user.uid);
              await updateDoc(userRef, {
                [`${assistantTitle}.Threads`]: arrayUnion(localThreadId),
              });
            }
          } else {
            throw new Error("Thread creation failed: No ID returned");
          }
        } catch (error) {
          console.error("Error creating thread:", error);
          removeStreamingPlaceholder();
          clearStreamingUi();
          handleAPIError(error, setMessages, updateThreadId, setIsSending);
          return;
        }
      } else {
        console.log(`Using existing thread: ${localThreadId}`);
      }

      try {
        // Pass the effective selected resource ID for PaliGPT
        console.log(`Creating message with resource: ${effectiveSelectedResource}`);
        startAssistantStream();
        await createMessage(localThreadId, fullMessage, assistantTitle, effectiveSelectedResource);
        
        // Use the selected resource's assistant ID for PaliGPT
        const effectiveAssistantId =
          chatbotType === 'paliGPT'
            ? effectiveSelectedResource
            : assistantId;
            
        console.log(`Creating run with assistant ID: ${effectiveAssistantId}`);
        createRun(
          localThreadId,
          effectiveAssistantId,
          handleNewMessage,
          handleError,
          assistantTitle,
          effectiveSelectedResource // Pass the effective selected resource ID
        );
      } catch (error) {
        console.error("Communication error:", error);
        removeStreamingPlaceholder();
        clearStreamingUi();
        handleAPIError(error, setMessages, updateThreadId, setIsSending);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      removeStreamingPlaceholder();
      clearStreamingUi();
      handleAPIError(error, setMessages, updateThreadId, setIsSending);
    }
  };

  const handleFileUpload = async (e) => {
    const isNurAlHuda = chatbotType === "nurAlHuda";
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;

    // 1) build local items (no UNKNOWN labels anymore)
    const items = incoming
      .filter(f => {
        if (f.size > MAX_FILE_SIZE) {
          setModalMessage(`${f.name} exceeds the 10MB size limit.`);
          setIsModalOpen(true);
          return false;
        }
        return true;
      })
      .map(file => {
        const ext = extFromName(file.name);
        const {kind, label} = classifyByExt(ext);
        const previewUrl = kind === "image" ? URL.createObjectURL(file) : null;
        return {
          id: crypto.randomUUID(),
          file, name: file.name, size: file.size, ext, kind, label,
          previewUrl, status: isNurAlHuda ? "uploading" : "local",
          fileId: null
        };
      });

    // append immediately (snappy UI)
    setUploadedFiles(prev => [...prev, ...items]);
    e.target.value = null;

    // 2) for prompt-ID path, upload to OpenAI and stamp fileIds
    if (isNurAlHuda && items.length) {
      const { uploadFiles } = await import("../services/openaiService");

      const fileList = items.map(x => x.file);
      // uploadFiles returns null for any file that failed — never throws
      const ids = await uploadFiles(fileList);

      let anyFailed = false;
      setUploadedFiles(prev =>
        prev.map(it => {
          const idx = items.findIndex(x => x.id === it.id);
          if (idx === -1) return it;
          if (ids[idx]) {
            return { ...it, status: "uploaded", fileId: ids[idx] };
          } else {
            anyFailed = true;
            return { ...it, status: "error" };
          }
        })
      );

      if (anyFailed) {
        setModalMessage("Some files failed to upload. You can retry or remove them.");
        setIsModalOpen(true);
      }
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prevFiles) => {
      const fileToRemove = prevFiles[index];
      // Clean up preview URL if it exists
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prevFiles.filter((_, i) => i !== index);
    });
  };

  const handleSelectPrompt = (prompt) => {
    if (totalPromptCount >= maxPrompts) {
      let message =
        accountType === "pro" || accountType === "enterprise"
          ? `You have reached the daily limit of ${maxPrompts} prompts. Wait until tomorrow to learn more!`
          : `You have reached the daily limit of ${maxPrompts} prompts. Upgrade to a premium or enterprise account for more prompts!`;
      setModalMessage(message);
      setIsModalOpen(true);
      return;
    }
    setCurrentMessage(prompt);
    handleSendMessage();
  };

  const [showScrollButton, setShowScrollButton] = useState(false);

  // Detect if user is at/near the bottom
  const handleScroll = () => {
    if (!scrollViewRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollViewRef.current;

    // If the user is scrolled all the way down, hide the button
    if (scrollHeight - scrollTop <= clientHeight + 2) {
      setShowScrollButton(false);
    } else {
      // If user is above bottom, show the button
      setShowScrollButton(true);
    }
  };

  const scrollToBottom = useCallback(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTop = scrollViewRef.current.scrollHeight;
    }
  }, []);

  // Whenever messages or streaming status changes, ensure we can auto-scroll if desired
  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending, isStreaming, liveStreamText, scrollToBottom]);

  const handleGoToHome = () => {
    // Clean up blob URLs to prevent memory leaks
    genImages.forEach(img => URL.revokeObjectURL(img.url));
    genImagesRef.current = [];
    setGenImages([]);
    navigate("/");
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const sendNegativeReport = (msgReported) => {
    console.log("Negative Report Clicked", msgReported);
    setReportedMessage(msgReported);
    setIsModalOpenReport(true);
  };

  const sendPositiveReport = async (reportedMessage) => {
    console.log("Positive Report Clicked", reportedMessage);
    if (user) {
      try {
        await addDoc(collection(db, "positiveReviews"), {
          message: reportedMessage,
          user: user.uid,
          timestamp: new Date(),
        });
        console.log("Positive report saved");
      } catch (error) {
        console.error("Error saving positive report:", error);
      }
    } else {
      console.log("User not logged in");
    }
  };

  const handleReportSubmit = async () => {
    console.log("Message reported\n", reportedMessage, reportFeedbackMessage);
    setIsModalOpenReport(false);
    if (user) {
      try {
        await addDoc(collection(db, "negativeReviews"), {
          message: reportedMessage,
          feedback: reportFeedbackMessage,
          user: user.uid,
          timestamp: new Date(),
        });
        console.log("Negative report saved");
      } catch (error) {
        console.error("Error saving negative report:", error);
      }
    } else {
      console.log("User not logged in");
    }
  };

  return (
    <div className={`chatscreen-container${chatbotType === 'paliGPT' ? ' palestine-theme' : ''}`}>
      {/* Lantern elements */}
      {showImage && LanternSvg && (
        <>
          <div className="lantern-container lantern-left">
            <img src={LanternSvg} alt="Lantern Left" />
          </div>
          <div className="lantern-container lantern-right">
            <img src={LanternSvg} alt="Lantern Right" />
          </div>
        </>
      )}

      {/* Modals */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalClose}
        confirmLabel="Close"
        closeLabel=""
        message={<p className="modal-message">{modalMessage}</p>}
      />
      <Modal
        isOpen={isModalOpenReport}
        onClose={() => setIsModalOpenReport(false)}
        onConfirm={handleReportSubmit}
        confirmLabel="Submit"
        closeLabel="Cancel"
        message={
          <>
            <p className="modal-message">Report this message:</p>
            <div className="modal-submessage-container">{reportedMessage}</div>
            <textarea
              className="modal-textarea"
              onChange={(e) => setReportFeedbackMessage(e.target.value)}
              placeholder="Describe the issue..."
              rows="4"
              cols="50"
            />
          </>
        }
      />

      <div className="chatscreen-header-container">
        {!user && (
          <button className="chatscreen-home-button" onClick={handleGoToHome}>
            Home
          </button>
        )}
        <div className="chatscreen-header-title">
          <span className="chatscreen-assistant-title">{assistantTitle}</span>
          {activeConversationId && conversationTitle && conversationTitle !== 'New Conversation' && (
            <span className="chatscreen-conv-title">{conversationTitle}</span>
          )}
        </div>
      </div>

      <div
        ref={scrollViewRef}
        className="chatscreen-messages-container"
        onScroll={handleScroll}
      >
        
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <div
              className={`chatscreen-message-wrapper ${
                message.sender === "user"
                  ? "user-message-wrapper"
                  : "bot-message-wrapper"
              }`}
            >
              {message.sender !== "user" && (
                <div className="bot-profile-and-actions">
                  <img
                    src={chatbotImage}
                    alt="Bot"
                    className="chatbot-profile-image"
                  />
                  {!message.streaming && (
                    <div className="bot-actions">
                      <button
                        className="thumb-button"
                        onClick={() => sendPositiveReport(message.text)}
                      >
                        👍
                      </button>
                      <button
                        className="thumb-button"
                        onClick={() => sendNegativeReport(message.text)}
                      >
                        👎
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div
                className={`chatscreen-message-container ${
                  message.sender === "user"
                    ? "chatscreen-user-message"
                    : "chatscreen-bot-message"
                }`}
              >
                {message.sender !== "user" && message.streaming && !liveStreamText ? (
                  <>
                    {isThinking && (
                      <div className="thinking-indicator">
                        <div className="thinking-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <div className="thinking-text">Thinking...</div>
                        {reasoningText && (
                          <div className="reasoning-preview">
                            {reasoningText.substring(0, 100)}...
                          </div>
                        )}
                      </div>
                    )}

                    {!isThinking && isProcessing && (
                      <div className="processing-indicator">
                        <div className="processing-spinner"></div>
                        <div className="processing-text">Generating image...</div>
                      </div>
                    )}

                    {!isThinking && !isProcessing && (
                      <div className="typing-indicator">
                        <div className="typing-indicator-dot"></div>
                        <div className="typing-indicator-dot"></div>
                        <div className="typing-indicator-dot"></div>
                      </div>
                    )}
                  </>
                ) : message.streaming ? (
                  <RenderMarkdown markdown={liveStreamText} />
                ) : (
                  <>
                    {message.files?.length > 0 && (
                      <div className="msg-file-chips">
                        {message.files.map((f, fi) => (
                          <span key={fi} className="msg-file-chip">
                            {f.kind === "image" ? <FaFileImage /> :
                             f.kind === "pdf"   ? <FaFilePdf /> :
                             f.kind === "ppt"   ? <FaFileAlt /> :
                             f.kind === "doc"   ? <FaFileWord /> :
                             f.kind === "xls"   ? <FaFileExcel /> : <FaPaperclip />}
                            {f.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {message.text && <RenderMarkdown markdown={message.text} />}
                  </>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
        
        {/* Generated Images for Nur Al Huda - Inside messages container */}
        {genImages.length > 0 && genImages.map((img, i) => (
          <div key={i} className="chatscreen-message-wrapper bot-message-wrapper">
            <div className="bot-profile-and-actions">
              <img
                src={chatbotImage}
                alt="Bot"
                className="chatbot-profile-image"
              />
            </div>
            <div className="chatscreen-message-container chatscreen-bot-message">
              <div className="generated-image">
                <img
                  src={img.url}
                  alt={img.caption || `generated-${i+1}`}
                />
                {img.caption && (
                  <div className="image-caption">{img.caption}</div>
                )}
                <div className="image-actions">
                  <a
                    href={img.url}
                    download={`generated-${i + 1}.png`}
                    className="download-button"
                  >
                    📥 Download
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scroll-to-bottom arrow button */}
      {showScrollButton && (
        <button className="scroll-to-bottom-button" onClick={scrollToBottom}>
          {isSending ? "Responding ↓" : "↓"}
        </button>
      )}

      <div>
        {showImage && chatbotImage && (
          <div className="chatscreen-message-image">
            <img
              src={chatbotImage}
              alt={`${assistantTitle} Image`}
              style={{ maxHeight: "200px", objectFit: "contain" }}
            />
          </div>
        )}
        {showFloatingPrompts ? (
          <FloatingPrompts
            onSelectPrompt={handleSelectPrompt}
            isSending={isSending}
            chatbotType={chatbotType}
            visible={showFloatingPrompts}
          />
        ) : (
          <SuggestedPrompts
            onSelectPrompt={handleSelectPrompt}
            isSending={isSending}
            chatbotType={chatbotType}
            className={messages.length > 0 ? "has-messages" : "initial-view"}
          />
        )}
      </div>

      {maxPrompts - totalPromptCount < 5 && (
        <div className="prompt-count-info">
          <p className="prompt-count-text">
            Prompts used today:{" "}
            {totalPromptCount > maxPrompts
              ? `${maxPrompts}/${maxPrompts}`
              : `${totalPromptCount}/${maxPrompts}`}
          </p>
          {accountType !== "pro" && totalPromptCount >= maxPrompts && (
            <span
              className="upgrade-message"
              onClick={() => navigate("/pricing")}
            >
              Upgrade for more prompts!
            </span>
          )}
        </div>
      )}

      {/* Add ActiveResourceDisplay for PaliGPT */}
      {chatbotType === 'paliGPT' && (
        <div style={{ padding: '0 20px', marginBottom: '16px' }}>
          <ActiveResourceDisplay
            selectedResource={selectedResource}
            onSelect={handleResourceChange}
          />
        </div>
      )}

      <div className="chatscreen-input-container">
        {/* Uploaded files bar — floats above the input */}
        {uploadedFiles.length > 0 && (
          <div className="nh-filebar">
            {uploadedFiles.map((it, idx) => (
              <FileChip
                key={it.id}
                item={it}
                onRemove={() => handleRemoveFile(idx)}
              />
            ))}
          </div>
        )}
        <label htmlFor="file-upload" className="custom-file-upload">
          <FaPaperclip />
        </label>
        <input
          id="file-upload"
          type="file"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          multiple
        />

        <textarea
          className="chatscreen-input chatscreen-textarea"
          value={currentMessage}
          onChange={(e) => {
            setCurrentMessage(e.target.value);
            // Auto-grow textarea
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
          }}
          placeholder="Type your message..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isSending) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          disabled={isSending}
          rows={1}
        />

        <button
          className="custom-mic"
          onClick={handleMicClick}
          disabled={isSending}
          title="Speak"
        >
          <FaMicrophone />
        </button>

        <button
          className="chatscreen-send-button"
          onClick={handleSendMessage}
          disabled={isSending || (!currentMessage.trim() && uploadedFiles.length === 0)}
          style={
            isSending || (!currentMessage.trim() && uploadedFiles.length === 0)
              ? { opacity: 0.4, cursor: "not-allowed" }
              : {}
          }
        >
          <FaArrowUp />
        </button>
      </div>

      {showAudioOverlay && (
        <AudioInterface
          assistantTitle={assistantTitle}
          onClose={handleAudioClose}
          onAudioSubmit={handleAudioSubmit}
          onResponseComplete={handleAudioResponseComplete}
        />
      )}
    </div>
  );
};

export default ChatScreen;
